import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import { ExecutionEnvironmentDescriptor } from "./environment.ts";
import {
  IsoDateTime,
  NonNegativeInt,
  PositiveInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";
import {
  KeybindingCommand,
  KeybindingValue,
  KeybindingWhen,
  ResolvedKeybindingsConfig,
} from "./keybindings.ts";
import { EditorId } from "./editor.ts";
import { ModelCapabilities } from "./model.ts";
import {
  ProviderDriverKind,
  ProviderInstanceId,
  ProviderInstanceOwnership,
} from "./providerInstance.ts";
import { ServerProviderUsageLimits } from "./providerUsageLimits.ts";
import { ServerSettings } from "./settings.ts";

const KeybindingsMalformedConfigIssue = Schema.Struct({
  kind: Schema.Literal("keybindings.malformed-config"),
  message: TrimmedNonEmptyString,
});

const KeybindingsInvalidEntryIssue = Schema.Struct({
  kind: Schema.Literal("keybindings.invalid-entry"),
  message: TrimmedNonEmptyString,
  index: Schema.Number,
});

export const ServerConfigIssue = Schema.Union([
  KeybindingsMalformedConfigIssue,
  KeybindingsInvalidEntryIssue,
]);
export type ServerConfigIssue = typeof ServerConfigIssue.Type;

const ServerConfigIssues = Schema.Array(ServerConfigIssue);

export const ServerProviderState = Schema.Literals(["ready", "warning", "error", "disabled"]);
export type ServerProviderState = typeof ServerProviderState.Type;

export const ServerProviderReadiness = Schema.Literals([
  "disabled",
  "missing",
  "broken",
  "initializing",
  "unauthenticated",
  "unknown",
  "authenticated",
  "blocked",
  "ready",
]);
export type ServerProviderReadiness = typeof ServerProviderReadiness.Type;

export const ServerProviderDiscoveryFailure = Schema.Literals(["timeout"]);
export type ServerProviderDiscoveryFailure = typeof ServerProviderDiscoveryFailure.Type;

export const ServerProviderDiagnostic = Schema.Struct({
  code: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
});
export type ServerProviderDiagnostic = typeof ServerProviderDiagnostic.Type;

export const ServerProviderAuthStatus = Schema.Literals([
  "authenticated",
  "unauthenticated",
  "unknown",
]);
export type ServerProviderAuthStatus = typeof ServerProviderAuthStatus.Type;

export const ServerProviderAuth = Schema.Struct({
  status: ServerProviderAuthStatus,
  type: Schema.optional(TrimmedNonEmptyString),
  label: Schema.optional(TrimmedNonEmptyString),
  email: Schema.optional(TrimmedNonEmptyString),
});
export type ServerProviderAuth = typeof ServerProviderAuth.Type;

export const AppOwnedCodexAuthenticationMethod = Schema.Literals([
  "chatgpt-browser",
  "chatgpt-device-code",
  "api-key",
]);
export type AppOwnedCodexAuthenticationMethod = typeof AppOwnedCodexAuthenticationMethod.Type;

/**
 * Non-secret, reconnect-safe authentication progress carried by the complete provider snapshot.
 * Browser URLs, device codes, login ids, and API keys are deliberately absent.
 */
export const AppOwnedCodexAuthenticationTransition = Schema.Struct({
  state: Schema.Literals([
    "idle",
    "starting",
    "validating-key",
    "waiting-for-user",
    "succeeded",
    "failed",
    "cancelled",
    "blocked",
  ]),
  method: Schema.optionalKey(AppOwnedCodexAuthenticationMethod),
  message: Schema.optionalKey(TrimmedNonEmptyString),
});
export type AppOwnedCodexAuthenticationTransition =
  typeof AppOwnedCodexAuthenticationTransition.Type;

export const ServerProviderModel = Schema.Struct({
  slug: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  shortName: Schema.optional(TrimmedNonEmptyString),
  subProvider: Schema.optional(TrimmedNonEmptyString),
  /** Names the model also answers to, so a stored selection survives a rename. */
  aliases: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  badge: Schema.optional(Schema.Literal("new")),
  isCustom: Schema.Boolean,
  isDefault: Schema.optional(Schema.Boolean),
  isLegacy: Schema.optional(Schema.Boolean),
  capabilities: Schema.NullOr(ModelCapabilities),
});
export type ServerProviderModel = typeof ServerProviderModel.Type;

export const ServerProviderSlashCommandInput = Schema.Struct({
  hint: TrimmedNonEmptyString,
});
export type ServerProviderSlashCommandInput = typeof ServerProviderSlashCommandInput.Type;

export const ServerProviderSlashCommand = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.optional(TrimmedNonEmptyString),
  input: Schema.optional(ServerProviderSlashCommandInput),
});
export type ServerProviderSlashCommand = typeof ServerProviderSlashCommand.Type;

export const ServerProviderSkill = Schema.Struct({
  name: TrimmedNonEmptyString,
  description: Schema.optional(TrimmedNonEmptyString),
  path: TrimmedNonEmptyString,
  scope: Schema.optional(TrimmedNonEmptyString),
  enabled: Schema.Boolean,
  usable: Schema.optional(Schema.Boolean),
  displayName: Schema.optional(TrimmedNonEmptyString),
  shortDescription: Schema.optional(TrimmedNonEmptyString),
  /**
   * The skill is hidden from the agent's own skill tool, so only the user can
   * start it — Claude Code's `disable-model-invocation`. Composers must offer
   * it as a slash command; naming it in prose does nothing.
   */
  userInvocationOnly: Schema.optional(Schema.Boolean),
  /**
   * The mirror of {@link ServerProviderSkill.userInvocationOnly}: Claude Code's
   * `user-invocable: false` keeps the skill out of its own slash commands, so
   * only the agent can start it. Composers must not offer it under `/`.
   */
  userInvocable: Schema.optional(Schema.Boolean),
  /** App is reserved for skills proven to come from the embedded Host Store inventory. */
  ownership: Schema.optional(Schema.Literals(["provider", "app"])),
  /**
   * What the composer's Action menu calls this skill, when a project or user
   * skill declares itself an Action with `afar-action: <label>` in its
   * SKILL.md frontmatter. App skills are Actions through the App's own catalog.
   */
  actionLabel: Schema.optional(TrimmedNonEmptyString),
});
export type ServerProviderSkill = typeof ServerProviderSkill.Type;

export const ServerProviderSkillCatalogInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  cwd: TrimmedNonEmptyString,
});
export type ServerProviderSkillCatalogInput = typeof ServerProviderSkillCatalogInput.Type;

export const ServerProviderSkillCatalogResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  cwd: TrimmedNonEmptyString,
  skills: Schema.Array(ServerProviderSkill),
});
export type ServerProviderSkillCatalogResult = typeof ServerProviderSkillCatalogResult.Type;

export class ServerProviderSkillCatalogError extends Schema.TaggedErrorClass<ServerProviderSkillCatalogError>()(
  "ServerProviderSkillCatalogError",
  {
    instanceId: ProviderInstanceId,
    cwd: TrimmedNonEmptyString,
    message: TrimmedNonEmptyString,
  },
) {}

/**
 * Availability of a configured provider instance from the runtime's POV.
 *
 *  - `available` — the build ships this driver and an instance is wired
 *    up. Default for legacy snapshots produced from the closed
 *    `ServerSettings.providers` map.
 *  - `unavailable` — the user's `ServerSettings.providerInstances` (or a
 *    persisted thread / session binding) references a driver this build
 *    doesn't ship. Common after rolling back from a fork or PR branch
 *    that introduced a new driver. The snapshot is preserved so the UI
 *    can render "missing driver" affordances and so the data round-trips
 *    when the user moves back to the fork.
 *
 * Snapshots with `availability: "unavailable"` MUST set
 * `installed: false` and `enabled: false`; the runtime refuses turn
 * starts against them with a structured error.
 */
export const ServerProviderAvailability = Schema.Literals(["available", "unavailable"]);
export type ServerProviderAvailability = typeof ServerProviderAvailability.Type;

export const ServerProviderContinuation = Schema.Struct({
  groupKey: TrimmedNonEmptyString,
});
export type ServerProviderContinuation = typeof ServerProviderContinuation.Type;

/** Native provider-history operations exposed to clients without driver-name inference. */
export const ServerProviderConversationCapabilities = Schema.Struct({
  threadFork: Schema.Literals(["native", "unsupported"]),
  conversationRevert: Schema.Literals(["native", "unsupported"]),
  threadGoal: Schema.Literals(["native", "unsupported"]),
  manualCompaction: Schema.Literals(["native", "unsupported"]),
  /**
   * Independent subagents, which the Review Action's peer council needs.
   * Optional so Environments from before Actions still decode; a client treats
   * an absent value as unknown and offers Review.
   */
  nativeSubagents: Schema.optionalKey(Schema.Literals(["native", "unsupported"])),
});
export type ServerProviderConversationCapabilities =
  typeof ServerProviderConversationCapabilities.Type;

/** Authoritative project/thread and lifecycle controls reported by one Provider Instance. */
export const ServerProviderInstanceCapabilities = Schema.Struct({
  mcpObservability: Schema.optionalKey(Schema.Boolean),
  threadCreation: Schema.Struct({
    generalChat: Schema.Boolean,
    /**
     * Can open a new AFAR Project on its first turn. The name predates
     * creation from references and stays so clients and Environments of
     * different versions still agree on it.
     */
    projectDefinition: Schema.Boolean,
  }),
  controls: Schema.Struct({
    configuration: Schema.Boolean,
    probing: Schema.Boolean,
    installation: Schema.Boolean,
    maintenance: Schema.Boolean,
    nativeAuthentication: Schema.Boolean,
  }),
});
export type ServerProviderInstanceCapabilities = typeof ServerProviderInstanceCapabilities.Type;

/** Environment-wide provider controls that are not attached to an existing instance row. */
export const ServerProviderManagementCapabilities = Schema.Struct({
  installAppOwnedProvider: Schema.Boolean,
  /**
   * AFAR manages this Environment's providers (a Cloud Workspace): clients cannot add, edit,
   * sign in to, update, or remove Provider Instances.
   */
  managedByAfar: Schema.optionalKey(Schema.Boolean),
});
export type ServerProviderManagementCapabilities = typeof ServerProviderManagementCapabilities.Type;

/**
 * Whether AFAR manages the Environment's Agent Providers, which is what makes it a Cloud Workspace
 * to a client (ADR-0130). A config that has not arrived yet reads as not managed.
 */
export function isManagedByAfar(
  config:
    | { readonly providerManagementCapabilities?: ServerProviderManagementCapabilities }
    | null
    | undefined,
): boolean {
  return config?.providerManagementCapabilities?.managedByAfar === true;
}

export const ServerProviderInstallation = Schema.Literals([
  "unknown",
  "not-installed",
  "installing",
  "installed",
  "failed",
  "broken",
]);
export type ServerProviderInstallation = typeof ServerProviderInstallation.Type;

export const ServerProviderLaunchability = Schema.Literals([
  "unknown",
  "launchable",
  "unlaunchable",
]);
export type ServerProviderLaunchability = typeof ServerProviderLaunchability.Type;

export const ProviderInstallationManager = Schema.Literals(["npm", "pnpm", "bun", "homebrew"]);
export type ProviderInstallationManager = typeof ProviderInstallationManager.Type;

/**
 * Non-secret evidence that ties a User-owned provider executable to the one
 * installer that owns it. Shared by every package-managed provider runtime, so
 * it names the package rather than assuming a particular one.
 */
export const ProviderInstallationProvenance = Schema.Struct({
  manager: ProviderInstallationManager,
  configuredExecutable: TrimmedNonEmptyString,
  resolvedExecutable: TrimmedNonEmptyString,
  realExecutable: TrimmedNonEmptyString,
  managerExecutable: TrimmedNonEmptyString,
  managerRealExecutable: TrimmedNonEmptyString,
  managedRoot: TrimmedNonEmptyString,
  packageName: TrimmedNonEmptyString,
  candidateVersion: Schema.optionalKey(TrimmedNonEmptyString),
  homebrewKind: Schema.optionalKey(Schema.Literals(["formula", "cask"])),
});
export type ProviderInstallationProvenance = typeof ProviderInstallationProvenance.Type;

export const ServerProviderRuntimeUpdateStatus = Schema.Literals([
  "unknown",
  "up-to-date",
  "update-available",
]);
export type ServerProviderRuntimeUpdateStatus = typeof ServerProviderRuntimeUpdateStatus.Type;

export const ServerProviderRuntimeUpdate = Schema.Struct({
  status: ServerProviderRuntimeUpdateStatus,
  canUpdate: Schema.Boolean,
  targetVersion: Schema.NullOr(TrimmedNonEmptyString),
  message: TrimmedNonEmptyString,
  provenance: Schema.optionalKey(ProviderInstallationProvenance),
});
export type ServerProviderRuntimeUpdate = typeof ServerProviderRuntimeUpdate.Type;

export const ServerProviderConfigurationEvidence = Schema.Struct({
  label: TrimmedNonEmptyString,
  value: TrimmedNonEmptyString,
});
export type ServerProviderConfigurationEvidence = typeof ServerProviderConfigurationEvidence.Type;

/** Last provider readback, scoped to its native session; never inferred from saved settings. */
export const ServerProvider = Schema.Struct({
  // Routing key for the configured instance this snapshot represents. This
  // is the only stable identity consumers may use for provider routing.
  instanceId: ProviderInstanceId,
  // Open driver kind slug that selects the implementation handling this
  // instance. It is metadata/capability context, not a routing key.
  driver: ProviderDriverKind,
  ownership: ProviderInstanceOwnership,
  displayName: Schema.optional(TrimmedNonEmptyString),
  accentColor: Schema.optional(TrimmedNonEmptyString),
  badgeLabel: Schema.optional(TrimmedNonEmptyString),
  continuation: Schema.optional(ServerProviderContinuation),
  // Optional for protocol compatibility. Consumers fail closed when an older
  // Environment omits this native-history capability declaration.
  conversationCapabilities: Schema.optionalKey(ServerProviderConversationCapabilities),
  // Optional capability evidence. Consumers fail closed
  // until the selected Environment reports the complete capability record.
  instanceCapabilities: Schema.optionalKey(ServerProviderInstanceCapabilities),
  showInteractionModeToggle: Schema.optional(Schema.Boolean),
  // The driver streams context window usage, so a started thread will have a
  // meter once its activities load. Clients reserve the meter's space on it.
  reportsContextWindow: Schema.optional(Schema.Boolean),
  requiresNewThreadForModelChange: Schema.optional(Schema.Boolean),
  supportsConversationRollback: Schema.optional(Schema.Boolean),
  enabled: Schema.Boolean,
  installation: ServerProviderInstallation,
  launchability: ServerProviderLaunchability,
  installed: Schema.Boolean,
  version: Schema.NullOr(TrimmedNonEmptyString),
  status: ServerProviderState,
  readiness: Schema.optional(ServerProviderReadiness),
  discoveryFailure: Schema.optionalKey(ServerProviderDiscoveryFailure),
  auth: ServerProviderAuth,
  authentication: Schema.optionalKey(AppOwnedCodexAuthenticationTransition),
  checkedAt: IsoDateTime,
  message: Schema.optional(TrimmedNonEmptyString),
  // Optional for back-compat: every legacy producer omits this field and
  // an absent value is interpreted as `"available"` by consumers (see
  // `isProviderAvailable`). New `ProviderInstanceRegistry` outputs set it
  // explicitly so the UI can render unavailable shadows from
  // `ServerSettings.providerInstances`.
  availability: Schema.optional(ServerProviderAvailability),
  // Human-readable reason populated when `availability === "unavailable"`.
  // Surfaces in the UI alongside the missing-driver affordance.
  unavailableReason: Schema.optional(TrimmedNonEmptyString),
  diagnostics: Schema.optional(Schema.Array(ServerProviderDiagnostic)),
  // Live subscription quota for the signed-in account. A driver with no notion
  // of subscription usage never sets it, so it has no Limits row at all rather
  // than an empty one.
  usageLimits: Schema.optional(ServerProviderUsageLimits),
  runtimeUpdate: Schema.optionalKey(ServerProviderRuntimeUpdate),
  configurationEvidence: Schema.Array(ServerProviderConfigurationEvidence),
  models: Schema.Array(ServerProviderModel),
  slashCommands: Schema.Array(ServerProviderSlashCommand).pipe(
    Schema.withDecodingDefault(Effect.succeed([])),
  ),
  skills: Schema.Array(ServerProviderSkill).pipe(Schema.withDecodingDefault(Effect.succeed([]))),
});
export type ServerProvider = typeof ServerProvider.Type;

export const ServerProviders = Schema.Array(ServerProvider);
export type ServerProviders = typeof ServerProviders.Type;

/**
 * Treat the optional `availability` as "available" when absent. This is
 * the rule legacy producers (which omit the field) and new producers
 * (which set it explicitly) agree on so consumers never have to thread
 * `?? "available"` defaults through their code paths.
 */
export const isProviderAvailable = (snapshot: ServerProvider): boolean =>
  snapshot.availability !== "unavailable";

export const ServerObservability = Schema.Struct({
  logsDirectoryPath: TrimmedNonEmptyString,
  localTracingEnabled: Schema.Boolean,
});
export type ServerObservability = typeof ServerObservability.Type;

export const ServerTraceDiagnosticsErrorKind = Schema.Literals([
  "trace-file-not-found",
  "trace-file-read-failed",
]);
export type ServerTraceDiagnosticsErrorKind = typeof ServerTraceDiagnosticsErrorKind.Type;

export const ServerTraceDiagnosticsSpanSummary = Schema.Struct({
  name: TrimmedNonEmptyString,
  count: NonNegativeInt,
  failureCount: NonNegativeInt,
  totalDurationMs: Schema.Number,
  averageDurationMs: Schema.Number,
  maxDurationMs: Schema.Number,
});
export type ServerTraceDiagnosticsSpanSummary = typeof ServerTraceDiagnosticsSpanSummary.Type;

export const ServerTraceDiagnosticsFailureSummary = Schema.Struct({
  name: TrimmedNonEmptyString,
  cause: TrimmedNonEmptyString,
  count: NonNegativeInt,
  lastSeenAt: Schema.DateTimeUtc,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsFailureSummary = typeof ServerTraceDiagnosticsFailureSummary.Type;

export const ServerTraceDiagnosticsRecentFailure = Schema.Struct({
  name: TrimmedNonEmptyString,
  cause: TrimmedNonEmptyString,
  durationMs: Schema.Number,
  endedAt: Schema.DateTimeUtc,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsRecentFailure = typeof ServerTraceDiagnosticsRecentFailure.Type;

export const ServerTraceDiagnosticsSpanOccurrence = Schema.Struct({
  name: TrimmedNonEmptyString,
  durationMs: Schema.Number,
  endedAt: Schema.DateTimeUtc,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsSpanOccurrence = typeof ServerTraceDiagnosticsSpanOccurrence.Type;

export const ServerTraceDiagnosticsLogEvent = Schema.Struct({
  spanName: TrimmedNonEmptyString,
  level: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
  seenAt: Schema.DateTimeUtc,
  traceId: TrimmedNonEmptyString,
  spanId: TrimmedNonEmptyString,
});
export type ServerTraceDiagnosticsLogEvent = typeof ServerTraceDiagnosticsLogEvent.Type;

export const ServerTraceDiagnosticsResult = Schema.Struct({
  traceFilePath: TrimmedNonEmptyString,
  scannedFilePaths: Schema.Array(TrimmedNonEmptyString),
  readAt: Schema.DateTimeUtc,
  recordCount: NonNegativeInt,
  parseErrorCount: NonNegativeInt,
  firstSpanAt: Schema.Option(Schema.DateTimeUtc),
  lastSpanAt: Schema.Option(Schema.DateTimeUtc),
  failureCount: NonNegativeInt,
  interruptionCount: NonNegativeInt,
  slowSpanThresholdMs: NonNegativeInt,
  slowSpanCount: NonNegativeInt,
  logLevelCounts: Schema.Record(TrimmedNonEmptyString, NonNegativeInt),
  topSpansByCount: Schema.Array(ServerTraceDiagnosticsSpanSummary),
  slowestSpans: Schema.Array(ServerTraceDiagnosticsSpanOccurrence),
  commonFailures: Schema.Array(ServerTraceDiagnosticsFailureSummary),
  latestFailures: Schema.Array(ServerTraceDiagnosticsRecentFailure),
  latestWarningAndErrorLogs: Schema.Array(ServerTraceDiagnosticsLogEvent),
  partialFailure: Schema.Option(Schema.Boolean),
  error: Schema.Option(
    Schema.Struct({
      kind: ServerTraceDiagnosticsErrorKind,
      message: TrimmedNonEmptyString,
    }),
  ),
});
export type ServerTraceDiagnosticsResult = typeof ServerTraceDiagnosticsResult.Type;

export const ServerProcessSignal = Schema.Literals(["SIGINT", "SIGKILL"]);
export type ServerProcessSignal = typeof ServerProcessSignal.Type;

export const ServerProcessDiagnosticsEntry = Schema.Struct({
  pid: PositiveInt,
  ppid: NonNegativeInt,
  pgid: Schema.Option(Schema.Int),
  status: TrimmedNonEmptyString,
  cpuPercent: Schema.Number,
  rssBytes: NonNegativeInt,
  elapsed: TrimmedNonEmptyString,
  command: TrimmedNonEmptyString,
  depth: NonNegativeInt,
  childPids: Schema.Array(PositiveInt),
});
export type ServerProcessDiagnosticsEntry = typeof ServerProcessDiagnosticsEntry.Type;

export const ServerProcessDiagnosticsResult = Schema.Struct({
  serverPid: PositiveInt,
  readAt: Schema.DateTimeUtc,
  processCount: NonNegativeInt,
  totalRssBytes: NonNegativeInt,
  totalCpuPercent: Schema.Number,
  processes: Schema.Array(ServerProcessDiagnosticsEntry),
  error: Schema.Option(
    Schema.Struct({
      message: TrimmedNonEmptyString,
    }),
  ),
});
export type ServerProcessDiagnosticsResult = typeof ServerProcessDiagnosticsResult.Type;

export const ServerProcessResourceHistoryInput = Schema.Struct({
  windowMs: NonNegativeInt,
  bucketMs: NonNegativeInt,
});
export type ServerProcessResourceHistoryInput = typeof ServerProcessResourceHistoryInput.Type;

export const ServerProcessResourceHistoryBucket = Schema.Struct({
  startedAt: Schema.DateTimeUtc,
  endedAt: Schema.DateTimeUtc,
  avgCpuPercent: Schema.Number,
  maxCpuPercent: Schema.Number,
  maxRssBytes: NonNegativeInt,
  maxProcessCount: NonNegativeInt,
});
export type ServerProcessResourceHistoryBucket = typeof ServerProcessResourceHistoryBucket.Type;

export const ServerProcessResourceHistorySummary = Schema.Struct({
  processKey: TrimmedNonEmptyString,
  pid: PositiveInt,
  ppid: NonNegativeInt,
  command: TrimmedNonEmptyString,
  depth: NonNegativeInt,
  isServerRoot: Schema.Boolean,
  firstSeenAt: Schema.DateTimeUtc,
  lastSeenAt: Schema.DateTimeUtc,
  currentCpuPercent: Schema.Number,
  avgCpuPercent: Schema.Number,
  maxCpuPercent: Schema.Number,
  cpuSecondsApprox: Schema.Number,
  currentRssBytes: NonNegativeInt,
  maxRssBytes: NonNegativeInt,
  sampleCount: NonNegativeInt,
});
export type ServerProcessResourceHistorySummary = typeof ServerProcessResourceHistorySummary.Type;

export const ServerProcessResourceHistoryFailureTag = Schema.Literals([
  "ProcessDiagnosticsQueryTimeoutError",
  "ProcessDiagnosticsQueryFailedError",
  "ProcessDiagnosticsServerProcessSignalError",
  "ProcessDiagnosticsNotDescendantError",
  "ProcessDiagnosticsSignalFailedError",
]);
export type ServerProcessResourceHistoryFailureTag =
  typeof ServerProcessResourceHistoryFailureTag.Type;

export const ServerProcessResourceHistoryResult = Schema.Struct({
  readAt: Schema.DateTimeUtc,
  windowMs: NonNegativeInt,
  bucketMs: NonNegativeInt,
  sampleIntervalMs: NonNegativeInt,
  retainedSampleCount: NonNegativeInt,
  totalCpuSecondsApprox: Schema.Number,
  buckets: Schema.Array(ServerProcessResourceHistoryBucket),
  topProcesses: Schema.Array(ServerProcessResourceHistorySummary),
  error: Schema.Option(
    Schema.Struct({
      failureTag: ServerProcessResourceHistoryFailureTag,
      message: TrimmedNonEmptyString,
    }),
  ),
});
export type ServerProcessResourceHistoryResult = typeof ServerProcessResourceHistoryResult.Type;

export const ServerSignalProcessInput = Schema.Struct({
  pid: PositiveInt,
  signal: ServerProcessSignal,
});
export type ServerSignalProcessInput = typeof ServerSignalProcessInput.Type;

export const ServerSignalProcessResult = Schema.Struct({
  pid: PositiveInt,
  signal: ServerProcessSignal,
  signaled: Schema.Boolean,
  message: Schema.Option(TrimmedNonEmptyString),
});
export type ServerSignalProcessResult = typeof ServerSignalProcessResult.Type;

/**
 * A palette the environment's machine publishes for AFAR to follow, read from
 * a theme file next to the rest of the environment's state. Two seed colors
 * rather than a full palette: clients derive the remaining roles with the same
 * generator the guided theme editor uses, so a desktop theme carries over as a
 * coherent AFAR palette instead of a foreign one.
 */
export const EnvironmentThemeColor = Schema.String.check(
  Schema.isPattern(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/),
);
export type EnvironmentThemeColor = typeof EnvironmentThemeColor.Type;

/**
 * Matches the client-side theme id rule, so a published id is selectable.
 * The appearance keywords are excluded outright: a published `dark.json`
 * would otherwise capture every client whose stored preference is the stock
 * `"dark"`, retinting people who never chose it.
 */
export const EnvironmentThemeId = Schema.String.check(
  Schema.isPattern(/^(?!(?:system|light|dark)$)[a-z0-9](?:[a-z0-9-]{0,47})$/),
);
export type EnvironmentThemeId = typeof EnvironmentThemeId.Type;

/**
 * Role colors as published. Values are any CSS color the client's theme
 * parser accepts (exported theme files use oklch), canonicalized client-side;
 * roles a build does not know are dropped there, so a machine may publish
 * roles a newer client added without breaking an older one. Keys must still
 * be role-shaped and values color-sized, so the record stays open to future
 * vocabulary without being an arbitrary-payload channel.
 */
const EnvironmentThemeColors = Schema.Record(
  Schema.String.check(Schema.isPattern(/^[a-zA-Z][a-zA-Z0-9]{0,63}$/)),
  TrimmedNonEmptyString.check(Schema.isMaxLength(64)),
);

const environmentThemeFields = {
  /**
   * Standard exported theme files (the Download button's output) carry
   * `version: 1`; the seeded short form a desktop generates has no version.
   */
  version: Schema.optional(Schema.Literal(1)),
  /** Shown on the theme card, e.g. the desktop theme's own name. */
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(48)),
  appearance: Schema.Literals(["light", "dark"]),
  /**
   * Seed colors. When present, clients derive the full palette from them with
   * the guided theme editor's generator and layer `colors` on top; when
   * absent, `colors` is the palette, as in an exported theme file.
   */
  canvas: Schema.optional(EnvironmentThemeColor),
  accent: Schema.optional(EnvironmentThemeColor),
  colors: Schema.optional(EnvironmentThemeColors),
  /** The other appearance's palette, as exported theme files carry it. */
  variants: Schema.optional(
    Schema.Struct({
      light: Schema.optional(EnvironmentThemeColors),
      dark: Schema.optional(EnvironmentThemeColors),
    }),
  ),
};

/**
 * One published theme file. The id is the filename, not part of the content,
 * so a file cannot claim another file's identity; an embedded `id` is ignored.
 */
export const EnvironmentThemeFile = Schema.Struct(environmentThemeFields);
export type EnvironmentThemeFile = typeof EnvironmentThemeFile.Type;

export const EnvironmentTheme = Schema.Struct({
  /** The publishing filename without its extension, stable across recolors. */
  id: EnvironmentThemeId,
  ...environmentThemeFields,
});
export type EnvironmentTheme = typeof EnvironmentTheme.Type;

/**
 * Whether a theme file carries anything to render. A file with neither seeds
 * nor colors would show as the stock palette wearing a name, which reads as a
 * bug rather than a theme - the CLI and the server watcher both reject it,
 * through this one predicate so they cannot drift.
 */
export function environmentThemeFileHasColors(file: EnvironmentThemeFile): boolean {
  return (
    (file.canvas !== undefined && file.accent !== undefined) ||
    (file.colors !== undefined && Object.keys(file.colors).length > 0)
  );
}

export const ServerConfig = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
  cwd: TrimmedNonEmptyString,
  keybindingsConfigPath: TrimmedNonEmptyString,
  keybindings: ResolvedKeybindingsConfig,
  issues: ServerConfigIssues,
  providers: ServerProviders,
  providerManagementCapabilities: Schema.optionalKey(ServerProviderManagementCapabilities),
  availableEditors: Schema.Array(EditorId),
  observability: ServerObservability,
  settings: ServerSettings,
  /**
   * Palettes published by this environment's machine. Never sent in a config
   * snapshot: the theme stream emits the current set before any change, so a
   * snapshot carrying it too would hand every subscriber the same array twice
   * per connect. Clients populate this by projecting `environmentThemesUpdated`.
   */
  environmentThemes: Schema.optional(Schema.Array(EnvironmentTheme)),
});
export type ServerConfig = typeof ServerConfig.Type;

const ServerUpsertKeybindingReplaceTarget = Schema.Struct({
  key: KeybindingValue,
  command: KeybindingCommand,
  when: Schema.optional(KeybindingWhen),
});

export const ServerUpsertKeybindingInput = Schema.Struct({
  key: KeybindingValue,
  command: KeybindingCommand,
  when: Schema.optional(KeybindingWhen),
  replace: Schema.optional(ServerUpsertKeybindingReplaceTarget),
});
export type ServerUpsertKeybindingInput = typeof ServerUpsertKeybindingInput.Type;

export const ServerRemoveKeybindingInput = ServerUpsertKeybindingReplaceTarget;
export type ServerRemoveKeybindingInput = typeof ServerRemoveKeybindingInput.Type;

export const ServerUpsertKeybindingResult = Schema.Struct({
  keybindings: ResolvedKeybindingsConfig,
  issues: ServerConfigIssues,
});
export type ServerUpsertKeybindingResult = typeof ServerUpsertKeybindingResult.Type;

export const ServerRemoveKeybindingResult = ServerUpsertKeybindingResult;
export type ServerRemoveKeybindingResult = typeof ServerRemoveKeybindingResult.Type;

export const ServerConfigUpdatedPayload = Schema.Struct({
  issues: ServerConfigIssues,
  providers: ServerProviders,
  settings: Schema.optional(ServerSettings),
});
export type ServerConfigUpdatedPayload = typeof ServerConfigUpdatedPayload.Type;

export const ServerConfigKeybindingsUpdatedPayload = Schema.Struct({
  keybindings: ResolvedKeybindingsConfig,
  issues: ServerConfigIssues,
});
export type ServerConfigKeybindingsUpdatedPayload =
  typeof ServerConfigKeybindingsUpdatedPayload.Type;

export const ServerConfigProviderStatusesPayload = Schema.Struct({
  providers: ServerProviders,
});
export type ServerConfigProviderStatusesPayload = typeof ServerConfigProviderStatusesPayload.Type;

export const ServerConfigSettingsUpdatedPayload = Schema.Struct({
  settings: ServerSettings,
});
export type ServerConfigSettingsUpdatedPayload = typeof ServerConfigSettingsUpdatedPayload.Type;

export const ServerConfigEnvironmentThemesUpdatedPayload = Schema.Struct({
  /** The full published set; empty once the machine publishes none. */
  themes: Schema.Array(EnvironmentTheme),
});
export type ServerConfigEnvironmentThemesUpdatedPayload =
  typeof ServerConfigEnvironmentThemesUpdatedPayload.Type;

export const ServerConfigStreamSnapshotEvent = Schema.Struct({
  type: Schema.Literal("snapshot"),
  config: ServerConfig,
});
export type ServerConfigStreamSnapshotEvent = typeof ServerConfigStreamSnapshotEvent.Type;

export const ServerConfigStreamKeybindingsUpdatedEvent = Schema.Struct({
  type: Schema.Literal("keybindingsUpdated"),
  payload: ServerConfigKeybindingsUpdatedPayload,
});
export type ServerConfigStreamKeybindingsUpdatedEvent =
  typeof ServerConfigStreamKeybindingsUpdatedEvent.Type;

export const ServerConfigStreamProviderStatusesEvent = Schema.Struct({
  type: Schema.Literal("providerStatuses"),
  payload: ServerConfigProviderStatusesPayload,
});
export type ServerConfigStreamProviderStatusesEvent =
  typeof ServerConfigStreamProviderStatusesEvent.Type;

export const ServerConfigStreamSettingsUpdatedEvent = Schema.Struct({
  type: Schema.Literal("settingsUpdated"),
  payload: ServerConfigSettingsUpdatedPayload,
});
export type ServerConfigStreamSettingsUpdatedEvent =
  typeof ServerConfigStreamSettingsUpdatedEvent.Type;

export const ServerConfigStreamEnvironmentThemesUpdatedEvent = Schema.Struct({
  type: Schema.Literal("environmentThemesUpdated"),
  payload: ServerConfigEnvironmentThemesUpdatedPayload,
});
export type ServerConfigStreamEnvironmentThemesUpdatedEvent =
  typeof ServerConfigStreamEnvironmentThemesUpdatedEvent.Type;

export const ServerConfigStreamEvent = Schema.Union([
  ServerConfigStreamSnapshotEvent,
  ServerConfigStreamKeybindingsUpdatedEvent,
  ServerConfigStreamProviderStatusesEvent,
  ServerConfigStreamSettingsUpdatedEvent,
  ServerConfigStreamEnvironmentThemesUpdatedEvent,
]);
export type ServerConfigStreamEvent = typeof ServerConfigStreamEvent.Type;

export const ServerLifecycleReadyPayload = Schema.Struct({
  at: IsoDateTime,
  environment: ExecutionEnvironmentDescriptor,
});
export type ServerLifecycleReadyPayload = typeof ServerLifecycleReadyPayload.Type;

export const ServerLifecycleWelcomePayload = Schema.Struct({
  environment: ExecutionEnvironmentDescriptor,
  cwd: TrimmedNonEmptyString,
  projectName: TrimmedNonEmptyString,
  bootstrapProjectId: Schema.optional(ProjectId),
  bootstrapThreadId: Schema.optional(ThreadId),
});
export type ServerLifecycleWelcomePayload = typeof ServerLifecycleWelcomePayload.Type;

export const ServerLifecycleStreamWelcomeEvent = Schema.Struct({
  sequence: NonNegativeInt,
  type: Schema.Literal("welcome"),
  payload: ServerLifecycleWelcomePayload,
});
export type ServerLifecycleStreamWelcomeEvent = typeof ServerLifecycleStreamWelcomeEvent.Type;

export const ServerLifecycleStreamReadyEvent = Schema.Struct({
  sequence: NonNegativeInt,
  type: Schema.Literal("ready"),
  payload: ServerLifecycleReadyPayload,
});
export type ServerLifecycleStreamReadyEvent = typeof ServerLifecycleStreamReadyEvent.Type;

export const ServerLifecycleStreamEvent = Schema.Union([
  ServerLifecycleStreamWelcomeEvent,
  ServerLifecycleStreamReadyEvent,
]);
export type ServerLifecycleStreamEvent = typeof ServerLifecycleStreamEvent.Type;

export const ServerProviderUpdatedPayload = Schema.Struct({
  providers: ServerProviders,
});
export type ServerProviderUpdatedPayload = typeof ServerProviderUpdatedPayload.Type;

/** Desktop-authorized App-owned Codex lifecycle commands. Filesystem paths are never client input. */
export const ManagedProviderCreateInput = Schema.Struct({
  displayName: Schema.optional(TrimmedNonEmptyString),
});
export type ManagedProviderCreateInput = typeof ManagedProviderCreateInput.Type;

export const ManagedProviderRetryInput = Schema.Struct({
  instanceId: ProviderInstanceId,
});
export type ManagedProviderRetryInput = typeof ManagedProviderRetryInput.Type;

export const ManagedProviderDeleteInput = ManagedProviderRetryInput;
export type ManagedProviderDeleteInput = ManagedProviderRetryInput;

export const ManagedProviderMutationResult = Schema.Struct({
  instanceId: ProviderInstanceId,
});
export type ManagedProviderMutationResult = typeof ManagedProviderMutationResult.Type;

export const AppOwnedCodexBrowserLoginInput = Schema.Struct({
  instanceId: ProviderInstanceId,
});
export type AppOwnedCodexBrowserLoginInput = typeof AppOwnedCodexBrowserLoginInput.Type;

export const AppOwnedCodexDeviceCodeLoginInput = AppOwnedCodexBrowserLoginInput;
export type AppOwnedCodexDeviceCodeLoginInput = AppOwnedCodexBrowserLoginInput;

export const AppOwnedCodexApiKeyLoginInput = Schema.Struct({
  instanceId: ProviderInstanceId,
  apiKey: Schema.String.check(Schema.isNonEmpty()),
});
export type AppOwnedCodexApiKeyLoginInput = typeof AppOwnedCodexApiKeyLoginInput.Type;

export const AppOwnedCodexAuthenticationCancelInput = AppOwnedCodexBrowserLoginInput;
export type AppOwnedCodexAuthenticationCancelInput = AppOwnedCodexBrowserLoginInput;

export const AppOwnedCodexLogoutInput = AppOwnedCodexBrowserLoginInput;
export type AppOwnedCodexLogoutInput = AppOwnedCodexBrowserLoginInput;

export const AppOwnedCodexAuthenticationOutcome = Schema.Literals([
  "waiting-for-user",
  "succeeded",
  "cancelled",
  "logged-out",
  "invalid",
  "unavailable",
  "indeterminate",
  "authenticated-but-blocked",
]);
export type AppOwnedCodexAuthenticationOutcome = typeof AppOwnedCodexAuthenticationOutcome.Type;

/** Direct command response. One-time browser/device evidence is never copied into provider state. */
export const AppOwnedCodexAuthenticationResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  method: Schema.optionalKey(AppOwnedCodexAuthenticationMethod),
  outcome: AppOwnedCodexAuthenticationOutcome,
  authUrl: Schema.optionalKey(TrimmedNonEmptyString),
  verificationUrl: Schema.optionalKey(TrimmedNonEmptyString),
  userCode: Schema.optionalKey(TrimmedNonEmptyString),
  message: Schema.optionalKey(TrimmedNonEmptyString),
});
export type AppOwnedCodexAuthenticationResult = typeof AppOwnedCodexAuthenticationResult.Type;

export const AppOwnedCodexAuthenticationErrorCode = Schema.Literals([
  "desktop-required",
  "instance-not-found",
  "unsupported-instance",
  "active-instance",
  "operation-conflict",
  "provider-failure",
]);
export type AppOwnedCodexAuthenticationErrorCode = typeof AppOwnedCodexAuthenticationErrorCode.Type;

export class AppOwnedCodexAuthenticationError extends Schema.TaggedErrorClass<AppOwnedCodexAuthenticationError>()(
  "AppOwnedCodexAuthenticationError",
  {
    code: AppOwnedCodexAuthenticationErrorCode,
    detail: TrimmedNonEmptyString,
    instanceId: ProviderInstanceId,
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export const ManagedProviderOperationErrorCode = Schema.Literals([
  "desktop-required",
  "store-unavailable",
  "invalid-instance",
  "operation-conflict",
  "filesystem-failure",
  "projection-delayed",
]);
export type ManagedProviderOperationErrorCode = typeof ManagedProviderOperationErrorCode.Type;

export class ManagedProviderOperationError extends Schema.TaggedErrorClass<ManagedProviderOperationError>()(
  "ManagedProviderOperationError",
  {
    code: ManagedProviderOperationErrorCode,
    detail: TrimmedNonEmptyString,
    instanceId: Schema.optional(ProviderInstanceId),
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export const ServerProviderUpdateInput = Schema.Struct({
  instanceId: ProviderInstanceId,
});
export type ServerProviderUpdateInput = typeof ServerProviderUpdateInput.Type;

export const ServerProviderUpdateOutcome = Schema.Literals([
  "succeeded",
  "failed",
  "manual-required",
]);
export type ServerProviderUpdateOutcome = typeof ServerProviderUpdateOutcome.Type;

export const ServerProviderUpdateResult = Schema.Struct({
  instanceId: ProviderInstanceId,
  outcome: ServerProviderUpdateOutcome,
  message: TrimmedNonEmptyString,
  providers: ServerProviders,
});
export type ServerProviderUpdateResult = typeof ServerProviderUpdateResult.Type;

export const ServerProviderUpdateFailureReason = Schema.Literals([
  "instance-not-found",
  "unsupported-instance",
  "desktop-only",
  "active-instance",
  "already-running",
]);
export type ServerProviderUpdateFailureReason = typeof ServerProviderUpdateFailureReason.Type;

export class ServerProviderUpdateError extends Schema.TaggedErrorClass<ServerProviderUpdateError>()(
  "ServerProviderUpdateError",
  {
    instanceId: ProviderInstanceId,
    reason: ServerProviderUpdateFailureReason,
    message: TrimmedNonEmptyString,
  },
) {}
